import { AccountDetailSchema, useEndpoint, useToastNotifications, useUtilities } from '@apparatix/composables';
import { NewAccountPayload, transformNewAccountResponse, PendingAccount } from '@apparatix/schemas/Account';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import {
  ApiResponseBase,
  CreateAccountResponse,
  UpdateAccountResponse,
  LazyLoadEvent,
  MetaDataResponse,
  AccountOverview,
} from '@apparatix/types/Account';
import {
  AccountSearchResponse,
  AccountTypes,
  AssociationAccount,
  AssociationContact,
  ConflictingAccount,
  ContactSearchResponse,
  HasPrimaryContact,
} from '@apparatix/types/Leads';
import { useToast } from 'primevue/usetoast';

export interface AccountSearchFields {
  name?: string;
  address?: string;
  type?: AccountTypes | AccountTypes[];
  mode?: 'includes' | 'startswith'
}

export const useAccounts = () => {
  const store = useAccountStore();
  const { showError } = useToastNotifications();
  const { arrayHasValues } = useUtilities();

  const metaDataEndpoint = useEndpoint<MetaDataResponse>({
    type: 'GET',
    url: '/crm/accounts/settings',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const createEndpoint = useEndpoint<CreateAccountResponse>({
    url: '/crm/accounts',
    type: 'POST',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const toast = useToast();

  const updateEndpoint = useEndpoint<UpdateAccountResponse>({
    type: 'PUT',
    url: '/crm/accounts',
    successField: 'data',
    onError: (response: UpdateAccountResponse) => {
      if (response.conflict) {
        toast.add({
          severity: 'error', summary: 'Action Not Allowed', detail: response.conflict, life: 180 * 1000,
        });
        return;
      }
      toast.add({
        severity: 'error', summary: 'Error', detail: response.error, life: 3000,
      });
    },
    onSuccess: response => {
      if (store.accountDetails.id !== response.data.accounts[0].id) {
        return;
      }

      store.accountDetails = response.data.accounts[0];

      if (arrayHasValues(response.data?.errors)) {
        response.data.errors.forEach(error => {
          showError(error, 'Error', 8000);
        });
      }

      if (store.selectedAccount && store.selectedAccount.c2Id === store.accountDetails.id) {
        store.selectedAccount.ownerName = store.accountDetails.owner.fullName;
        store.selectedAccount.primaryContact = store.accountDetails.primaryContact;
        store.selectedAccount.accountPhone = store.accountDetails.accountPhone ?? '';
        store.selectedAccount.primaryContactPhone = store.accountDetails.primaryContactPhone ?? '';
        store.selectedAccount.primaryContactEmail = store.accountDetails.primaryContactEmail ?? '';
        store.selectedAccount.address = store.accountDetails.addresses[0].fullAddress;
        store.selectedAccount.c2Name = store.accountDetails.name;
        store.selectedAccount.markets = store.accountDetails.markets.map(m => m.market);
        store.selectedAccount.c2Sic = store.accountDetails.industry.description;
        store.selectedAccount.accountLabels = store.accountDetails.labels ?? [];
      }
    },
    toast: { showOnSuccess: false },
  });

  const searchEndpoint = useEndpoint<AccountSearchResponse>({
    type: 'GET',
    url: '',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const agencyAdvertiserEndpoint = useEndpoint<AccountSearchResponse>({
    type: 'GET',
    url: '',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const accountContactEndpoint = useEndpoint<ContactSearchResponse>({
    type: 'GET',
    url: '',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const hasPrimaryContactEndpoint = useEndpoint<ApiResponseBase<HasPrimaryContact>>({
    type: 'GET',
    url: '',
    successField: 'data',
    toast: { showOnError: true },
  });

  type LoadAccountsResponse = {
    data: {
      accounts: AccountOverview[],
    },
    pagination: {
      total: number,
      perPage: number,
      currentPage: number,
    }
  };

  const loadAccountsEndpoint = useEndpoint<LoadAccountsResponse>({
    type: 'POST',
    url: '/crm/accounts/filter',
    successField: 'data',
    cancelPreviousRequestOnSubsequent: true,
    toast: {
      showOnSuccess: false,
    },
  });

  const createAccount = async (payload: NewAccountPayload): Promise<Partial<AccountDetailSchema> & { type: string, displayType: string }> => {
    const response = await createEndpoint.getResults(payload);
    if (!response?.data?.account) {
      return undefined;
    }

    try {
      const transformed = transformNewAccountResponse.parse(response.data.account);
      return transformed;
    } catch {
      return undefined;
    }
  };

  const updateAccount = async (payload: any) => {
    await updateEndpoint.makeRequest(payload);
  };

  const searchAccounts = async ({
    name, address, type, mode = 'includes',
  }: AccountSearchFields, resultsCallback: (results: AssociationAccount[]) => void) => {
    if (!name && !address && !type) return;

    const parameters = new URLSearchParams({ mode: mode });
    if (name) {
      parameters.append('name', name);
    }
    if (address) {
      parameters.append('address', address);
    }
    if (type) {
      if (Array.isArray(type)) {
        parameters.append('type', type.join(','));
      } else {
        parameters.append('type', type);
      }
    }

    const url = `/crm/account/search?${parameters.toString()}`;
    searchEndpoint.setEndpointUrl(url);

    const results = await searchEndpoint.getResults();

    if (typeof resultsCallback === 'function') {
      resultsCallback(results.data.accounts);
    }
  };

  const getAdvertisersByAgency = async (agencyId: number, resultsCallback: (results: AssociationAccount[]) => void) => {
    const url = `/crm/accounts/${agencyId}/advertisers`;
    agencyAdvertiserEndpoint.setEndpointUrl(url);
    const response = await agencyAdvertiserEndpoint.getResults();
    if (typeof resultsCallback === 'function') {
      resultsCallback(response.data.accounts);
    }
  };

  const getContactsByAdvertiser = async (advertiserId: number, resultsCallback: (results: AssociationContact[]) => void) => {
    const url = `/crm/accounts/${advertiserId}/contacts`;
    accountContactEndpoint.setEndpointUrl(url);
    const response = await accountContactEndpoint.getResults();
    if (typeof resultsCallback === 'function') {
      resultsCallback(response.data.contacts);
    }
  };

  const getHasPrimaryContact = async (advertiserId: number, resultsCallback: (results: HasPrimaryContact) => void) => {
    const url = `/crm/accounts/${advertiserId}/primary-contact/validation`;
    hasPrimaryContactEndpoint.setEndpointUrl(url);
    const response = await hasPrimaryContactEndpoint.getResults();
    if (typeof resultsCallback === 'function') {
      resultsCallback(response.data);
    }
  };

  const loadAccountsMetaData = async () => await metaDataEndpoint.getResults();

  const loadAccounts = async (event: LazyLoadEvent) => {
    store.isTableLoading = true;

    const pageNumber = Math.floor(event.first / event.pageSize) + 1;
    const payload = {
      filter: event.filters,
      page: pageNumber,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
      pageSize: event.pageSize,
    };

    try {
      const response = await loadAccountsEndpoint.getResults(payload);

      if (response?.data?.accounts) {
        store.accounts = response.data.accounts;
      }

      if (response?.pagination?.total) {
        store.totalRecords = response.pagination.total;
      }

      store.totalRecords = response?.pagination?.total ?? 0;
    } catch {
      // TODO @Kalen toast error to user here?
      console.error('Error loading paged accounts');
    } finally {
      store.isTableLoading = false;
    }
  };

  // ignoreLoadStatus will force the data to be re-fetched. Otherwise it will only fetch data if there are no accounts in the store
  const loadAccountsHome = async (ignoreLoadStatus: boolean = false) => {
    store.isLoading = true;
    if (store.accounts?.length === 0 || ignoreLoadStatus) {
      const initialEvent: LazyLoadEvent = {
        first: 0,
        pageSize: store.rowsPerPage,
        sortField: null,
        sortOrder: null,
        filters: store.tableFilters,
      };
      loadAccounts(initialEvent);
      const response = await loadAccountsMetaData();
      store.aeLimit = response.data.relatedData.aeLimits;
      store.users = response.data.relatedData.users;
      store.paymentOptions = response.data.relatedData.contactOptions;
      store.labelOptions = response.data.relatedData.accountLabels;
      store.canViewAELimits = response.data.permissions.some((permission) => permission.tag === 'hasAeLimits');
      store.canAddAccounts = response.data.permissions.some((permission) => permission.tag === 'hasCreateAccount');
      store.canChangeOwner = response.data.permissions.some((permission) => permission.tag === 'hasChangeOwner');
      store.canOwnAgencies = response.data.permissions.some((permission) => permission.tag === 'hasAgencyOwnership');
      store.canCreateProposalsFromAccount = response.data.permissions.some((permission) => permission.tag === 'createProposalFromAccount');
      store.useCrmCampaigns = response.data.permissions.some((permission) => permission.tag === 'useCrmCampaigns');
      store.accountOwners = response.data.relatedData.accountOwners;
      store.industryCodes = response.data.relatedData.sic;
      store.prevAccountOwners = response.data.relatedData.prevAccountOwners;
      store.accountOffices = response.data.relatedData.accountOffices;
      store.accountTypes = response.data.relatedData.accountTypes;
      store.isLoading = false;
    } else {
      store.isLoading = false;
    }
  };

  const setConflictLevel = (conflict: AssociationAccount, newAccount: PendingAccount) => {
    const doAddressCheck = Array.isArray(conflict.addresses) && conflict.addresses[0] && Array.isArray(newAccount.addresses) && newAccount.addresses[0];
    if (!doAddressCheck) {
      (conflict as ConflictingAccount).isHardConflict = false;
      return;
    }

    const [conflictAddress] = conflict.addresses;
    const [existingAddress] = newAccount.addresses;

    const sameStreet = conflictAddress.address_line1?.toLowerCase() === existingAddress.addressLine1?.toLowerCase();
    const sameCity = conflictAddress.city?.toLowerCase() === existingAddress.city?.toLowerCase();
    const sameState = conflictAddress.region?.toLowerCase() === existingAddress.region?.toLowerCase();
    const sameZip = conflictAddress.postal_code?.toLowerCase() === existingAddress.postalCode?.toLowerCase();

    (conflict as ConflictingAccount).isHardConflict = sameStreet && sameCity && sameState && sameZip;
  };

  return {
    createAccount,
    getAdvertisersByAgency,
    getContactsByAdvertiser,
    getHasPrimaryContact,
    loadAccounts,
    loadAccountsHome,
    searchAccounts,
    setConflictLevel,
    updateAccount,
  };
};
